import { useCallback, useContext, useRef } from "react";

import { QuestionBan } from "types/socket-messages";
import { SqsSocketTypes, Status } from "@/types/working-model";
import {
	appendOneQuestionsComment,
	handleBan as handleBanAction,
	prependQuestions,
	questionCommentApprove,
	questionCommentDelete,
	questionCommentUnapprove,
	questionDelete,
	questionLike,
	questionUnapprove,
	questionUnlike,
	questionUpsert,
	questionsAnsweredBulk,
	QuestionsContext,
} from "../../../panel/contexts/engage/questions-state";
import { assertIsAnsweredQuestion, assertIsDeleteQuestionComment, assertIsPaginatedQuestion, assertIsMessage, assertIsQuestionBan, assertIsQuestionLike, assertIsQuestionSocketDeleteType, assertIsQuestionStatusData, assertIsSocketQuestionComment } from "./question-socket.assertions";

/**
 * Returns a stable `onMessage` callback suitable for passing to `useQuestionsSocketsAdmin`.
 * Because the callback never changes reference, the socket hook's effect won't re-run on
 * every parent render, preventing repeated listener teardown/re-registration.
 */
const useOnIncomingSocketMessages = (channel: number | undefined) => {
	const [, dispatch] = useContext(QuestionsContext);

	// Keep dispatch and channel in a ref so the stable callback always reads the latest
	// values without needing to be recreated.
	const stateRef = useRef({ dispatch, channel });
	stateRef.current = { dispatch, channel };

	return useCallback((type: string, data: unknown) => {
		const { dispatch: d, channel: ch } = stateRef.current;

		switch (type) {
			case SqsSocketTypes.QUESTION: {
				try {
					assertIsPaginatedQuestion(data);
				} catch (error) {
					console.error(error);
					return;
				}
				d(prependQuestions([data]));
				break;
			}

			case SqsSocketTypes.QUESTION_COMMENT: {
				try {
					assertIsSocketQuestionComment(data);
				} catch (error) {
					console.error(error);
					return;
				}
				d(appendOneQuestionsComment(data.question_id, [data]));
				break;
			}

			case SqsSocketTypes.QUESTION_LIKE: {
				try {
					assertIsQuestionLike(data);
				} catch (error) {
					console.error(error);
					return;
				}
				const likeId = data.question_id;
				d(questionLike(likeId));
				break;
			}

			case SqsSocketTypes.QUESTION_UNLIKE: {
				try {
					assertIsQuestionLike(data);
				} catch (error) {
					console.error(error);
					return;
				}
				const unlikeId = data.question_id;
				d(questionUnlike(unlikeId));
				break;
			}

			case 'delete_question': {
				try {
					assertIsMessage(data);
					assertIsQuestionSocketDeleteType(data.data);
				} catch (error) {
					console.error(error);
					return;
				}
				d(questionDelete(data.data.id));
				break;
			}

			case 'undelete_question': {
				try {
					assertIsMessage(data);
					assertIsPaginatedQuestion(data.data);
				} catch (error) {
					console.error(error);
					return;
				}
				d(questionUpsert(data.data));
				break;
			}

			case 'delete_question_comment': {
				try {
					assertIsMessage(data);
					assertIsDeleteQuestionComment(data.data);
				} catch (error) {
					console.error(error);
					return;
				}
				d(questionCommentDelete(data.data.comment_id, data.data.question_id));
				break;
			}

			case 'undelete_question_comment': {
				try {
					assertIsMessage(data);
					assertIsSocketQuestionComment(data.data);
				} catch (error) {
					console.error(error);
					return;
				}
				d(appendOneQuestionsComment(data.data.question_id, [data.data]));
				break;
			}

			case 'bulk_answer': {
				try {
					assertIsMessage(data);
					assertIsAnsweredQuestion(data.data);
				} catch (error) {
					console.error(error);
					return;
				}
				d(questionsAnsweredBulk(data.data.question_ids, data.data.answered));
				break;
			}

			case 'ban': {
				try {
					assertIsMessage(data);
				} catch (error) {
					console.error(error);
					return;
				}
				if (!Array.isArray(data.data)) return;
				const validBans: QuestionBan[] = [];
				for (const item of data.data) {
					try {
						assertIsQuestionBan(item);
						validBans.push(item);
					} catch (error) {
						console.error(error);
					}
				}
				if (validBans.length) d(handleBanAction(validBans));
				break;
			}

			case 'question_status': {
				try {
					assertIsMessage(data);
					assertIsPaginatedQuestion(data.data);
				} catch (error) {
					console.error(error);
					return;
				}
				if (data.data.status === Status.Approved) {
					d(questionUpsert(data.data));
				} else {
					d(questionUnapprove(data.data.id));
				}
				break;
			}

			case 'question_comment_status': {
				try {
					assertIsMessage(data);
					assertIsQuestionStatusData(data.data);
				} catch (error) {
					console.error(error);
					return;
				}
				if (data.data.status === Status.Approved) {
					d(questionCommentApprove(data.data.id));
				} else {
					d(questionCommentUnapprove(data.data.id));
				}
				break;
			}

			default:
				break;
		}
		// Do not pass any deps arrays in order to keep this stable — all values read through stateRef
	}, []);
};

export default useOnIncomingSocketMessages;
